import React from 'react';
import { ExternalLink, Newspaper } from 'lucide-react';
import { Button } from './ui/button';

const MediaRecognition = () => {
  const mediaFeatures = [
    {
      title: 'The Guardian',
      headline: "'He's a hero': the teacher who hand-delivered 15,000 free school meals in lockdown",
      excerpt: "Zane Powles walked for miles every day during lockdown to ensure vulnerable children didn't go hungry.",
      url: 'https://www.theguardian.com/lifeandstyle/2021/nov/13/hes-a-hero-the-teacher-who-hand-delivered-15000-free-school-meals-in-lockdown',
      date: 'November 2021',
      logo: 'The Guardian'
    },
    {
      title: 'Twinkl',
      headline: 'A Wave of Thanks for Educators - Zane\'s Story',
      excerpt: 'Deputy Headteacher shares his year delivering thousands of meals to children through all lockdowns, walking over 900 miles.',
      url: 'https://www.twinkl.co.uk/blog/a-wave-of-thanks-for-educators-zanes-story',
      date: 'May 2021',
      logo: 'Twinkl'
    },
    {
      title: 'BBC News',
      headline: 'Teacher who delivered free school meals gets MBE',
      excerpt: "A teacher who delivered thousands of free school meals to vulnerable children during lockdown has been awarded an MBE.",
      url: 'https://www.bbc.co.uk/news/uk-england-humber-56295722',
      date: 'March 2021',
      logo: 'BBC News'
    },
    {
      title: 'BBC News',
      headline: 'Zane Powles receives MBE from Princess Anne at Windsor Castle',
      excerpt: 'The former Grenadier Guardsman collected his MBE from Princess Anne after walking more than 900 miles delivering 15,000 meals.',
      url: 'https://www.bbc.co.uk/news/uk-england-humber-59152465',
      date: 'November 2021',
      logo: 'BBC News'
    },
    {
      title: 'ABC News (Good Morning America)',
      headline: 'Teacher delivers 78 free school lunches to kids every day',
      excerpt: "A UK teacher is making sure his students don't go without during the coronavirus pandemic.",
      url: 'https://abcnews.go.com/GMA/Living/teacher-delivers-78-free-school-lunches-kids-day/story?id=70182982',
      date: 'April 2020',
      logo: 'ABC News'
    },
    {
      title: 'Schools Week',
      headline: '11 special Covid-19 gongs for schools sector in Queen\'s birthday honours',
      excerpt: 'Zane Powles, the assistant headteacher who made headlines delivering meals on foot, receives the MBE.',
      url: 'https://schoolsweek.co.uk/11-special-covid-19-gongs-for-schools-sector-in-queens-birthday-honours/',
      date: 'October 2020',
      logo: 'Schools Week'
    },
    {
      title: 'The Mirror',
      headline: 'Hero teacher gives Tories lesson in compassion with 15,000 meals delivered',
      excerpt: "Zane Powles personally delivered free school lunches to vulnerable children during the pandemic.",
      url: 'https://www.mirror.co.uk/news/uk-news/hero-teacher-gives-tories-lesson-23314918',
      date: '2020',
      logo: 'The Mirror'
    },
    {
      title: 'The Times',
      headline: 'My pupils ate cheese sandwiches for 17 weeks',
      excerpt: "The teacher who delivered thousands of meals during lockdown speaks about the reality of food poverty.",
      url: 'https://www.thetimes.com/uk/education/article/my-pupils-ate-cheese-sandwiches-for-17-weeks-5vszr3h9x',
      date: '2021',
      logo: 'The Times'
    }
  ];

  const podcasts = [
    {
      title: 'Always Better Than Yesterday Podcast',
      host: 'ABTY',
      description: 'Zane Powles MBE discusses his journey from the Grenadier Guards to educational leadership, and the story behind delivering 15,000 meals.',
      url: 'https://abty.co.uk/blog/f/zane-powles-mbe-on-the-always-better-than-yesterday-podcast'
    },
    {
      title: 'Teaching Today Podcast',
      host: 'Teaching Today',
      description: 'Episode 14: Zane Powles shares insights on teaching, community service, and going above and beyond for students during challenging times.',
      url: 'https://podcasts.apple.com/us/podcast/14-zane-powles-uk/id1511096682?i=1000489569508'
    },
    {
      title: 'Heart Radio: Teachers Uncut',
      host: 'Heart Radio',
      description: 'Candid conversation about the realities of teaching, the COVID-19 pandemic response, and supporting vulnerable children and families.',
      url: 'https://www.globalplayer.com/podcasts/episodes/7DrbUbd/'
    },
    {
      title: '5 Years Time Podcast',
      host: '5 Years Time',
      description: 'Episode 5: Zane Powles MBE reflects on his remarkable service, the impact of community care, and his vision for education\'s future.',
      url: 'https://music.amazon.de/podcasts/894fc1ee-d110-4113-823b-23e1aaa4dc95/episodes/f6e55049-0db4-496e-a7f3-1d86c904bd47/5-years-time-episode-5-zane-powles-mbe'
    }
  ];

  return (
    <section id="media-recognition" className="media-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Media Recognition</h2>
          <p className="section-subtitle">Featured in national and international media for educational leadership and community service</p>
        </div>

        <div className="media-grid">
          {mediaFeatures.map((feature, index) => (
            <div key={index} className="media-card">
              <div className="media-logo">
                <Newspaper size={24} />
                <span>{feature.logo}</span>
              </div>
              <h3 className="media-headline">{feature.headline}</h3>
              <p className="media-excerpt">{feature.excerpt}</p>
              <div className="media-footer">
                <span className="media-date">{feature.date}</span>
                <Button 
                  variant="link" 
                  onClick={() => window.open(feature.url, '_blank')}
                  className="media-link"
                >
                  Read Article <ExternalLink size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="podcast-section">
          <h3 className="subsection-title">Podcast Appearances</h3>
          {podcasts.map((podcast, index) => (
            <div key={index} className="podcast-card">
              <div className="podcast-content">
                <h4>{podcast.title}</h4>
                <p className="podcast-host">with {podcast.host}</p>
                <p className="podcast-description">{podcast.description}</p>
                <Button 
                  onClick={() => window.open(podcast.url, '_blank')}
                  className="podcast-button"
                >
                  Listen to Podcast <ExternalLink size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="alumni-feature">
          <div className="alumni-card">
            <h3>Grimbarians Alumni Feature</h3>
            <p>Featured in the Grimbarians alumni network, celebrating achievements and contributions to the community.</p>
            <Button 
              variant="outline"
              onClick={() => window.open('https://www.grimbarians.com/grimbarians/zane-poles', '_blank')}
            >
              View Feature <ExternalLink size={16} />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MediaRecognition;